import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  salaryProcessApi,
  MONTH_OPTIONS,
  YEAR_OPTIONS,
  GENDER_OPTIONS,
  STATUS_OPTIONS,
  SALARY_PROCESS_TOOLS,
  SALARY_PREDEFINED_REPORTS,
  SALARY_REPORT_COLUMNS_MAP,
  getMonthName,
} from '../api/salaryProcessApi';
import { appToast } from '../../../../components/AppToast';
import { appConfirm } from '../../../../components/AppConfirmModal';
import { useExportJob } from '../../../../hooks/useExportJob';
import { executePrintReport } from '../../../../utils/printReportUtil';

export function useSalaryProcessList() {
  const today = useMemo(() => new Date(), []);

  // Filter states
  const [companyFilter, setCompanyFilter] = useState(0);
  const [branchFilter, setBranchFilter] = useState(0);
  const [departmentFilter, setDepartmentFilter] = useState(0);
  const [employeeFilter, setEmployeeFilter] = useState(0);
  const [empGroupFilter, setEmpGroupFilter] = useState(0);
  const [genderFilter, setGenderFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [monthFilter, setMonthFilter] = useState(today.getMonth() + 1);
  const [yearFilter, setYearFilter] = useState(today.getFullYear());
  const [searchText, setSearchText] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 20;

  // Data & Aggregates
  const [records, setRecords] = useState([]);
  const [totalGross, setTotalGross] = useState(0);
  const [totalDeductions, setTotalDeductions] = useState(0);
  const [totalNet, setTotalNet] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Dynamic filter options loaded from backend
  const [options, setOptions] = useState({
    companies: [{ value: 0, label: 'All Companies' }],
    branches: [{ value: 0, label: 'All Branches' }],
    departments: [{ value: 0, label: 'All Departments' }],
    groups: [{ value: 0, label: 'All Groups' }],
    genders: GENDER_OPTIONS,
  });

  // Modals & Drawers
  const [isReportsDrawerOpen, setIsReportsDrawerOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isJournalModalOpen, setIsJournalModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isRollbackModalOpen, setIsRollbackModalOpen] = useState(false);
  const [isPayslipModalOpen, setIsPayslipModalOpen] = useState(false);
  const [isFormatModalOpen, setIsFormatModalOpen] = useState(false);
  const [selectedRecordForPayslip, setSelectedRecordForPayslip] = useState(null);

  // Background export job via JobEngine
  const { triggerExport, isExporting } = useExportJob({
    module: 'salary_process',
    jobType: 'EXPORT',
    fileFormat: 'XLSX',
  });

  // Active filters memo
  const activeFilters = useMemo(
    () => ({
      month: monthFilter,
      year: yearFilter,
      companyId: companyFilter || 0,
      branchId: branchFilter || 0,
      deptId: departmentFilter || 0,
      empId: employeeFilter || 0,
      empGroupId: empGroupFilter || 0,
      gender: genderFilter,
      status: statusFilter,
      search: searchText,
    }),
    [
      monthFilter,
      yearFilter,
      companyFilter,
      branchFilter,
      departmentFilter,
      employeeFilter,
      empGroupFilter,
      genderFilter,
      statusFilter,
      searchText,
    ]
  );

  // Load dynamic filter options from backend
  useEffect(() => {
    salaryProcessApi
      .getFilterOptions()
      .then((data) => {
        if (data) {
          setOptions({
            companies: data.companies || [{ value: 0, label: 'All Companies' }],
            branches: data.branches || [{ value: 0, label: 'All Branches' }],
            departments: data.departments || [{ value: 0, label: 'All Departments' }],
            groups: data.groups || [{ value: 0, label: 'All Groups' }],
            genders: data.genders || GENDER_OPTIONS,
          });
        }
      })
      .catch(() => {
        // Silently fall back to defaults
      });
  }, []);

  // Load salary records
  const loadRecords = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await salaryProcessApi.getRecords({ ...activeFilters, page, pageSize });
      setRecords(data.items || []);
      setTotalCount(data.total_count || 0);
      setTotalPages(data.total_pages || 1);
      setTotalGross(data.total_gross || 0);
      setTotalDeductions(data.total_deductions || 0);
      setTotalNet(data.total_net || 0);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.message ||
        (typeof err?.response?.data?.detail === 'string' ? err?.response?.data?.detail : null) ||
        err?.message ||
        'Failed to load salary records.';
      appToast.error(msg);
    } finally {
      setIsLoading(false);
    }
  }, [activeFilters, page, pageSize]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const handleSearch = () => {
    setPage(1);
    loadRecords();
  };

  const resetFilters = () => {
    setCompanyFilter(0);
    setBranchFilter(0);
    setDepartmentFilter(0);
    setEmployeeFilter(0);
    setEmpGroupFilter(0);
    setGenderFilter('All');
    setStatusFilter('All');
    setMonthFilter(today.getMonth() + 1);
    setYearFilter(today.getFullYear());
    setSearchText('');
    setPage(1);
  };

  const handleStatusTabChange = (key) => {
    setStatusFilter(key);
    setPage(1);
  };

  // Single employee payslip trigger
  const handleOpenSinglePayslip = (record) => {
    setSelectedRecordForPayslip(record);
    setIsPayslipModalOpen(true);
  };

  // ---------------------------------------------------------------------------
  // REPORT ACTION HANDLER (PredefinedReportsDrawer)
  // ---------------------------------------------------------------------------
  const handleReportAction = async (actionKey, reportKey) => {
    if (actionKey === 'print') {
      let repCols = SALARY_REPORT_COLUMNS_MAP[reportKey] || SALARY_REPORT_COLUMNS_MAP.paysheet_report;
      const reportDef = SALARY_PREDEFINED_REPORTS.find((r) => r.reportKey === reportKey);
      const repTitle = reportDef?.title || 'Salary Report';

      // Apply saved column preferences from ReportColumnsModal (localStorage)
      const savedColsJson = localStorage.getItem(`report_cols_${reportKey}`);
      if (savedColsJson) {
        try {
          const savedCols = JSON.parse(savedColsJson);
          const visibleSaved = savedCols.filter((c) => c.visible);
          if (visibleSaved.length > 0) {
            const reordered = [];
            visibleSaved.forEach((sc) => {
              const matched = repCols.find((rc) => rc.label === sc.label || rc.key === sc.key);
              if (matched) reordered.push(matched);
            });
            if (reordered.length > 0) repCols = reordered;
          }
        } catch {
          // Fallback to default columns
        }
      }

      const monthLabel = getMonthName(monthFilter);
      executePrintReport({
        title: `${repTitle} — ${monthLabel} ${yearFilter}`,
        columns: repCols,
        metadata: {
          Month: monthLabel,
          Year: String(yearFilter),
          Report: repTitle,
          'Status Filter': statusFilter || 'All',
        },
        fetchReportData: async () => {
          const res = await salaryProcessApi.fetchReportData(reportKey, activeFilters);
          return { items: res.items || [] };
        },
        onExportPdf: () => triggerExport({ ...activeFilters, report_key: reportKey }, 'PDF'),
        onExportExcel: () => triggerExport({ ...activeFilters, report_key: reportKey }, 'XLSX'),
        threshold: 2000,
      });
    } else if (actionKey === 'excel') {
      await triggerExport({ ...activeFilters, report_key: reportKey }, 'XLSX');
    } else if (actionKey === 'pdf') {
      await triggerExport({ ...activeFilters, report_key: reportKey }, 'PDF');
    }
  };

  // ---------------------------------------------------------------------------
  // TOOL SELECT HANDLER (Print, Export Excel, Export PDF, Rollback, Format, etc.)
  // ---------------------------------------------------------------------------
  const handleToolSelect = async (toolKey) => {
    if (toolKey === 'upload') {
      setIsUploadModalOpen(true);
    } else if (toolKey === 'print' || toolKey === 'print-report') {
      setIsReportsDrawerOpen(true);
    } else if (toolKey === 'export_excel') {
      await triggerExport(activeFilters, 'XLSX');
    } else if (toolKey === 'export_pdf') {
      await triggerExport(activeFilters, 'PDF');
    } else if (toolKey === 'journal') {
      setIsJournalModalOpen(true);
    } else if (toolKey === 'payment') {
      setIsPaymentModalOpen(true);
    } else if (toolKey === 'payslip') {
      setIsPayslipModalOpen(true);
    } else if (toolKey === 'format') {
      setIsFormatModalOpen(true);
    } else if (toolKey === 'rollback') {
      const monthLabel = getMonthName(monthFilter);
      const confirmed = await appConfirm({
        title: 'Rollback Salary Process',
        message: `Are you sure you want to rollback salary process for ${monthLabel} ${yearFilter}? This will delete all generated payslips for this period. If JV or Payment has been created, rollback will be blocked.`,
        confirmText: 'Rollback Process',
        variant: 'danger',
        icon: 'history',
      });
      if (!confirmed) return;
      try {
        const res = await salaryProcessApi.rollbackProcess({
          month: monthFilter,
          year: yearFilter,
          companyId: companyFilter || 0,
          branchId: branchFilter || 0,
          deptId: departmentFilter || 0,
        });
        appToast.success(
          res.message || `Salary process for ${monthLabel} ${yearFilter} rolled back successfully.`
        );
        loadRecords();
      } catch (err) {
        const msg =
          err?.response?.data?.detail?.message ||
          (typeof err?.response?.data?.detail === 'string' ? err?.response?.data?.detail : null) ||
          err?.message ||
          'Failed to rollback salary process.';
        appToast.error(msg);
      }
    }
  };

  return {
    state: {
      records,
      isLoading,
      isExporting,
      companyFilter,
      branchFilter,
      departmentFilter,
      employeeFilter,
      empGroupFilter,
      genderFilter,
      statusFilter,
      monthFilter,
      yearFilter,
      searchText,
      page,
      pageSize,
      totalCount,
      totalPages,
      totalGross,
      totalDeductions,
      totalNet,
      // Modals
      isReportsDrawerOpen,
      isUploadModalOpen,
      isJournalModalOpen,
      isPaymentModalOpen,
      isRollbackModalOpen,
      isPayslipModalOpen,
      isFormatModalOpen,
      selectedRecordForPayslip,
      // Options
      companyOptions: options.companies,
      branchOptions: options.branches,
      departmentOptions: options.departments,
      groupOptions: options.groups,
      genderOptions: options.genders,
      monthOptions: MONTH_OPTIONS,
      yearOptions: YEAR_OPTIONS,
      statusOptions: STATUS_OPTIONS,
      tools: SALARY_PROCESS_TOOLS,
      predefinedReports: SALARY_PREDEFINED_REPORTS,
    },
    actions: {
      setCompanyFilter,
      setBranchFilter,
      setDepartmentFilter,
      setEmployeeFilter,
      setEmpGroupFilter,
      setGenderFilter,
      setStatusFilter,
      setMonthFilter,
      setYearFilter,
      setSearchText,
      setPage,
      handleSearch,
      resetFilters,
      handleStatusTabChange,
      handleToolSelect,
      handleReportAction,
      handleOpenSinglePayslip,
      setIsReportsDrawerOpen,
      setIsUploadModalOpen,
      setIsJournalModalOpen,
      setIsPaymentModalOpen,
      setIsRollbackModalOpen,
      setIsPayslipModalOpen,
      setIsFormatModalOpen,
      refresh: loadRecords,
    },
  };
}
